import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import DemoTypeCard from './components/DemoTypeCard';
import CompanySizeSelector from './components/CompanySizeSelector';
import RoleSelector from './components/RoleSelector';
import BenefitCard from './components/BenefitCard';
import TestimonialCard from './components/TestimonialCard';
import TimeSlotSelector from './components/TimeSlotSelector';
import ProgressIndicator from './components/ProgressIndicator';
import ConfirmationSummary from './components/ConfirmationSummary';

const RequestDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    demoType: '',
    companySize: '',
    role: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    preferredDate: '',
    preferredTime: '',
    timezone: 'America/New_York',
    additionalInfo: '',
    agreeToTerms: false,
    subscribeNewsletter: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const steps = [
  { id: 'type', label: 'Demo Type' },
  { id: 'company', label: 'Company Info' },
  { id: 'contact', label: 'Contact Details' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'confirm', label: 'Confirm' }];


  const demoTypes = [
  {
    value: 'executive',
    title: 'Executive Briefing',
    description: 'Strategic overview for C-suite decision-makers focusing on business impact and ROI',
    icon: 'Presentation',
    features: ['30-45 minutes', 'Business impact focus', 'ROI analysis', 'Custom solutions']
  },
  {
    value: 'technical',
    title: 'Technical Demo',
    description: 'In-depth product walkthrough for policy monitoring teams and managers',
    icon: 'Monitor',
    features: ['60 minutes', 'Platform deep-dive', 'Integration options', 'Q&A session']
  },
  {
    value: 'evaluation',
    title: 'Evaluation Session',
    description: 'Comprehensive assessment for procurement teams conducting vendor comparison',
    icon: 'ClipboardCheck',
    features: ['90 minutes', 'Security review', 'Policy monitoring audit', 'Implementation plan']
  },
  {
    value: 'partner',
    title: 'Partnership Discussion',
    description: 'API capabilities and integration opportunities for technology partners',
    icon: 'Handshake',
    features: ['45 minutes', 'API overview', 'Integration support', 'Partnership terms']
  }];


  const companySizes = [
  { value: 'startup', label: 'Startup', range: '1-50 employees', icon: 'Rocket' },
  { value: 'smb', label: 'Small Business', range: '51-200 employees', icon: 'Building' },
  { value: 'midmarket', label: 'Mid-Market', range: '201-1000 employees', icon: 'Building2' },
  { value: 'enterprise', label: 'Enterprise', range: '1000+ employees', icon: 'Factory' }];


  const roles = [
  {
    value: 'executive',
    label: 'C-Suite Executive',
    description: 'CEO, CFO, COO, or other executive leadership',
    icon: 'Crown'
  },
  {
    value: 'policy-monitoring',
    label: 'Policy Monitoring Lead',
    description: 'Medical policy tracking and risk management',
    icon: 'ShieldCheck'
  },
  {
    value: 'legal',
    label: 'Legal Counsel',
    description: 'Legal department and policy affairs',
    icon: 'Scale'
  },
  {
    value: 'procurement',
    label: 'Procurement Manager',
    description: 'Vendor evaluation and purchasing decisions',
    icon: 'ShoppingCart'
  },
  {
    value: 'technical',
    label: 'Technical Lead',
    description: 'IT, engineering, or technical integration',
    icon: 'Code'
  },
  {
    value: 'consultant',
    label: 'Consultant',
    description: 'External advisor or consulting services',
    icon: 'Users'
  }];


  const benefits = [
  {
    icon: 'Shield',
    title: 'Proven Enterprise Reliability',
    description: '13 years of trusted service to Fortune 500 medical device manufacturers including Stryker, Medtronic, and Boston Scientific'
  },
  {
    icon: 'Zap',
    title: 'Real-Time Policy Monitoring',
    description: 'Never miss critical policy changes with automated alerts and comprehensive tracking across all major payers'
  },
  {
    icon: 'TrendingUp',
    title: 'Measurable ROI',
    description: 'Average 40% reduction in policy research time and 95% faster policy change identification'
  },
  {
    icon: 'Lock',
    title: 'Enterprise-Grade Security',
    description: 'SOC 2 Type II certified with HIPAA standards and enterprise SSO integration'
  }];


  const testimonials = [
  {
    quote: 'PolicyAlerts transformed our policy monitoring workflow. We went from reactive scrambling to proactive strategy. The ROI was evident within the first quarter.',
    name: 'Sarah Mitchell',
    role: 'VP of Policy Affairs',
    company: 'Leading Cardiovascular Device Manufacturer',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca9b4e24-1763293534250.png",
    avatarAlt: 'Professional woman with blonde hair in navy business suit smiling confidently at camera'
  },
  {
    quote: 'The executive briefing was exactly what we needed - clear business impact, no technical jargon. Implementation was seamless with our existing systems.',
    name: 'Michael Chen',
    role: 'Chief Policy Officer',
    company: 'Fortune 500 Medical Device Company',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a94acfd5-1763299528067.png",
    avatarAlt: 'Asian male executive with short black hair wearing dark suit and glasses in professional office setting'
  }];


  const timeSlots = [
  { value: '09:00', label: '9:00 AM', available: true },
  { value: '10:00', label: '10:00 AM', available: true },
  { value: '11:00', label: '11:00 AM', available: false },
  { value: '12:00', label: '12:00 PM', available: true },
  { value: '13:00', label: '1:00 PM', available: true },
  { value: '14:00', label: '2:00 PM', available: true },
  { value: '15:00', label: '3:00 PM', available: false },
  { value: '16:00', label: '4:00 PM', available: true }];


  const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }];


  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 0) {
      if (!formData?.demoType) newErrors.demoType = 'Please select a demo type';
    } else if (currentStep === 1) {
      if (!formData?.companySize) newErrors.companySize = 'Please select company size';
      if (!formData?.role) newErrors.role = 'Please select your role';
      if (!formData?.company) newErrors.company = 'Company name is required';
    } else if (currentStep === 2) {
      if (!formData?.firstName) newErrors.firstName = 'First name is required';
      if (!formData?.lastName) newErrors.lastName = 'Last name is required';
      if (!formData?.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData?.phone) newErrors.phone = 'Phone number is required';
    } else if (currentStep === 3) {
      if (!formData?.preferredDate) newErrors.preferredDate = 'Please select a date';
      if (!formData?.preferredTime) newErrors.preferredTime = 'Please select a time';
    } else if (currentStep === 4) {
      if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, steps?.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setIsSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Demo Request Confirmed - PolicyAlerts</title>
          <meta
            name="description"
            content="Your demo request has been confirmed. We'll be in touch shortly." />

        </Helmet>
        <div className="min-h-screen flex flex-col bg-background">
          <Header />
          <main className="flex-1 pt-24 pb-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 mb-6">
                  <Icon name="CheckCircle2" size={40} color="var(--color-success)" />
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                  Demo Request Confirmed!
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Thank you for your interest in PolicyAlerts. We've received your demo request and will be in touch shortly.
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6 md:p-8 mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-6">What Happens Next?</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon name="Mail" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Confirmation Email</h3>
                      <p className="text-sm text-muted-foreground">
                        You'll receive a confirmation email at {formData?.email} within the next few minutes with your demo details.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon name="Calendar" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Calendar Invite</h3>
                      <p className="text-sm text-muted-foreground">
                        A calendar invitation will be sent for your scheduled demo on {formData?.preferredDate} at {formData?.preferredTime}.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon name="FileText" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Pre-Demo Materials</h3>
                      <p className="text-sm text-muted-foreground">
                        We'll send preparation materials and an agenda to help you get the most out of your demo session.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon name="Phone" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Personal Contact</h3>
                      <p className="text-sm text-muted-foreground">
                        A dedicated account executive will reach out to {formData?.phone} to confirm details and answer any questions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="outline" iconName="Home" iconPosition="left" asChild>
                  <a href="/homepage">Return to Homepage</a>
                </Button>
                <Button variant="default" iconName="FileText" iconPosition="left" asChild>
                  <a href="/api-documentation">Explore API Docs</a>
                </Button>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>);

  }

  return (
    <>
      <Helmet>
        <title>Request Demo - PolicyAlerts | Medical Policy Monitoring Platform</title>
        <meta
          name="description"
          content="Schedule a personalized demo of PolicyAlerts. Choose from executive briefings, technical demos, or evaluation sessions. Trusted by Fortune 500 medical device manufacturers." />

      </Helmet>
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Request Your Personalized Demo
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
                Experience how PolicyAlerts transforms medical policy monitoring for enterprise organizations. Choose the demo format that best fits your needs.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <div className="lg:col-span-2">
                <div className="bg-card border border-border rounded-lg p-6 md:p-8">
                  <ProgressIndicator steps={steps} currentStep={currentStep} />

                  {currentStep === 0 &&
                  <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Select Your Demo Type
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Choose the demo format that aligns with your role and evaluation needs
                      </p>
                      <div className="space-y-4">
                        {demoTypes?.map((type) =>
                      <DemoTypeCard
                        key={type?.value}
                        type={type}
                        isSelected={formData?.demoType === type?.value}
                        onClick={() => handleInputChange('demoType', type?.value)} />

                      )}
                      </div>
                      {errors?.demoType &&
                    <p className="text-sm text-error mt-2">{errors?.demoType}</p>
                    }
                    </div>
                  }

                  {currentStep === 1 &&
                  <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Company Information
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Help us understand your organization to personalize your demo experience
                      </p>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-3">
                            Company Size
                          </label>
                          <CompanySizeSelector
                          sizes={companySizes}
                          selectedSize={formData?.companySize}
                          onSelect={(value) => handleInputChange('companySize', value)} />

                          {errors?.companySize &&
                        <p className="text-sm text-error mt-2">{errors?.companySize}</p>
                        }
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-3">
                            Your Role
                          </label>
                          <RoleSelector
                          roles={roles}
                          selectedRole={formData?.role}
                          onSelect={(value) => handleInputChange('role', value)} />

                          {errors?.role &&
                        <p className="text-sm text-error mt-2">{errors?.role}</p>
                        }
                        </div>
                        <Input
                        label="Company Name"
                        type="text"
                        placeholder="Enter your company name"
                        value={formData?.company}
                        onChange={(e) => handleInputChange('company', e?.target?.value)}
                        error={errors?.company}
                        required />

                      </div>
                    </div>
                  }

                  {currentStep === 2 &&
                  <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Contact Details
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        We'll use this information to send your demo confirmation and materials
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                        label="First Name"
                        type="text"
                        placeholder="John"
                        value={formData?.firstName}
                        onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                        error={errors?.firstName}
                        required />

                        <Input
                        label="Last Name"
                        type="text"
                        placeholder="Doe"
                        value={formData?.lastName}
                        onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                        error={errors?.lastName}
                        required />

                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Input
                        label="Work Email"
                        type="email"
                        placeholder="john.doe@company.com"
                        value={formData?.email}
                        onChange={(e) => handleInputChange('email', e?.target?.value)}
                        error={errors?.email}
                        required />

                        <Input
                        label="Phone Number"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={formData?.phone}
                        onChange={(e) => handleInputChange('phone', e?.target?.value)}
                        error={errors?.phone}
                        required />

                      </div>
                    </div>
                  }

                  {currentStep === 3 &&
                  <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Schedule Your Demo
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Select your preferred date and time. We'll send a calendar invitation with all details.
                      </p>
                      <div className="space-y-6">
                        <Input
                        label="Preferred Date"
                        type="date"
                        value={formData?.preferredDate}
                        onChange={(e) => handleInputChange('preferredDate', e?.target?.value)}
                        error={errors?.preferredDate}
                        required
                        min={new Date()?.toISOString()?.split('T')?.[0]} />

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-3">
                            Preferred Time
                          </label>
                          <TimeSlotSelector
                          slots={timeSlots}
                          selectedSlot={formData?.preferredTime}
                          onSelect={(value) => handleInputChange('preferredTime', value)} />

                          {errors?.preferredTime &&
                        <p className="text-sm text-error mt-2">{errors?.preferredTime}</p>
                        }
                        </div>
                        <Select
                        label="Timezone"
                        options={timezoneOptions}
                        value={formData?.timezone}
                        onChange={(value) => handleInputChange('timezone', value)} />

                        <Input
                        label="Additional Information (Optional)"
                        type="text"
                        placeholder="Any specific topics or questions you'd like to cover?"
                        value={formData?.additionalInfo}
                        onChange={(e) => handleInputChange('additionalInfo', e?.target?.value)} />

                      </div>
                    </div>
                  }

                  {currentStep === 4 &&
                  <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Review & Confirm
                      </h2>
                      <p className="text-sm text-muted-foreground mb-6">
                        Please review your information before submitting your demo request
                      </p>
                      <ConfirmationSummary
                      formData={formData}
                      demoTypes={demoTypes}
                      companySizes={companySizes}
                      roles={roles} />

                      <div className="mt-6 space-y-4">
                        <Checkbox
                        label="I agree to the Terms of Service and Privacy Policy"
                        checked={formData?.agreeToTerms}
                        onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
                        required />

                        {errors?.agreeToTerms &&
                      <p className="text-sm text-error">{errors?.agreeToTerms}</p>
                      }
                        <Checkbox
                        label="Subscribe to PolicyAlerts newsletter for policy updates and insights"
                        checked={formData?.subscribeNewsletter}
                        onChange={(e) =>
                        handleInputChange('subscribeNewsletter', e?.target?.checked)
                        } />

                      </div>
                    </div>
                  }

                  <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-border">
                    {currentStep > 0 &&
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      iconName="ChevronLeft"
                      iconPosition="left"
                      fullWidth>

                        Back
                      </Button>
                    }
                    {currentStep < steps?.length - 1 ?
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      fullWidth>

                        Continue
                      </Button> :

                    <Button
                      variant="default"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="right"
                      fullWidth>

                        Submit Request
                      </Button>
                    }
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Why Choose PolicyAlerts?
                  </h3>
                  <div className="space-y-4">
                    {benefits?.map((benefit, index) =>
                    <BenefitCard key={index} benefit={benefit} />
                    )}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    What Our Clients Say
                  </h3>
                  <div className="space-y-4">
                    {testimonials?.map((testimonial, index) =>
                    <TestimonialCard key={index} testimonial={testimonial} />
                    )}
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <Icon name="Phone" size={24} color="var(--color-primary)" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        Need Immediate Assistance?
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Our sales team is available to answer your questions
                      </p>
                      <Button variant="outline" size="sm" iconName="Phone" iconPosition="left">
                        Call Sales: 1-800-POLICY-1
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>);

};

export default RequestDemo;